/*
 * BLANC SPACE collection description disclosure
 *
 * The full Shopify description stays in the document for customers, search
 * engines and assistive technology. Only the initial visual presentation is
 * clamped, and a control is added when the copy genuinely exceeds two lines.
 */

if (!customElements.get('collection-description')) {
  customElements.define(
    'collection-description',
    class CollectionDescription extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.text = this.querySelector('[data-collection-description-text]');
        this.toggle = this.querySelector('[data-collection-description-toggle]');
        this.label = this.querySelector('[data-collection-description-label]');

        if (!this.text || !this.toggle || !this.label) return;

        // Labels come from the theme's locale files via data attributes so
        // secondary languages translate; English remains the fallback.
        this.moreLabel = this.label.dataset.labelMore || 'Show more';
        this.lessLabel = this.label.dataset.labelLess || 'Show less';

        this.toggle.addEventListener('click', () => this.toggleExpanded());
        this.resizeObserver = new ResizeObserver(() => this.syncOverflow());
        this.resizeObserver.observe(this.text);
        this.setAttribute('data-ready', '');
        requestAnimationFrame(() => this.syncOverflow());
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      syncOverflow() {
        // Comparing scrollHeight to the clamped clientHeight (rather than
        // 2 × line-height) accounts for paragraph margins, so a description
        // that already fits two rendered lines gets no control. While
        // expanded, re-clamp synchronously (no paint in between) to measure
        // whether the control is still needed after a resize.
        const wasExpanded = this.hasAttribute('data-expanded');
        if (wasExpanded) this.removeAttribute('data-expanded');
        const needsDisclosure = this.text.scrollHeight > this.text.clientHeight + 1;
        if (wasExpanded && needsDisclosure) {
          this.setAttribute('data-expanded', '');
          return;
        }

        this.toggle.hidden = !needsDisclosure;

        if (!needsDisclosure) {
          this.removeAttribute('data-expanded');
          this.toggle.setAttribute('aria-expanded', 'false');
          this.label.textContent = this.moreLabel;
        }
      }

      toggleExpanded() {
        const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';

        this.toggle.setAttribute('aria-expanded', String(!isExpanded));
        this.toggleAttribute('data-expanded', !isExpanded);
        this.label.textContent = isExpanded ? this.moreLabel : this.lessLabel;
      }
    }
  );
}
